// Luxon-free: a unit-less `Duration.as('month')` is a casual 30-day month.
const MS_PER_MONTH = 30 * 24 * 60 * 60 * 1000;

// Local-midnight equivalent of `DateTime.fromISO('YYYY-MM-DD')`.
const localDate = (year: number, month: number, day: number) =>
  new Date(year, month - 1, day);

/**
 * The goal of the functions in this file are to estimate my PERSONAL e levels over time.
 *
 * Timeline shows the following:
 * 14/06/2024 - 88 pmol/L
 * 22/06/2025 -   4x Pump Estrogel
 * 05/08/2025 -   1x Patch every 3.5 Days
 * 05/09/2024 - 435 pmol/L
 * 05/12/2024 - 739 pmol/L
 * 24/02/2025 -   1x 100mg Pellet Implant
 * 27/05/2025 - 259 pmol/L
 * 05/07/2025 - 276 pmol/L
 * 22/07/2025 -   2x 100mg Pellet Implant
 * 18/09/2025 - 754 pmol/L
 * 21/01/2026 - 678 pmol/L
 * 27/01/2026 -   2x 100mg Pellet Implant
 * 20/05/2026 - 1224 pmol/L
 *
 * How implants are different from injections:
 *    Injections are an ester of estradiol. Ester slowly diffuses out, and gets cleaved into free estradiol
 *    3 Steps. Diffuse -> Cleave -> Eliminate
 *
 *    Implants are pure crystalline estradiol. The pellet slowly dissolves from its surface entering the bloodstream
 *    2 Steps: Dissolve -> Eliminate
 *
 * How come pellets have a sudden rise and fall?
 *    The pellet doesn't release at a full rate instantly! The insertion site needs to heal for ~2-3 months.
 *    More blood supply -> Faster absorption
 *    Afterwards, the pellet has shrunk enough that release rate drops - this is called a Generalized Bateman function (??).
 * */

/**
 * These are magic numbers from Claude using papers!!!!!!! Bellow is their explanation.
 * Fit derivation using papers:
 *   50mg  → 347 pmol/L  ──┐
 *   100mg → 602 pmol/L  ──┴─ solve: 602/347 = 2^α → α = log(1.737)/log(2) ≈ 0.797
 *   Then: PEAK_SCALE = 347 / 50^0.797 ≈ 13.02
 */
const PEAK_SCALE = 13.02;
const PEAK_EXPONENT = 0.797;

const peakFromDose = (dose: number) => {
  return PEAK_SCALE * Math.pow(dose, PEAK_EXPONENT);
};

// Needed to add since subsequent implants we're sticking around too long!
// Higher = Faster decay
const DECAY_RATE = 0.3;
const eFromImplant = (monthsSinceInsertion: number, dose: number) => {
  if (monthsSinceInsertion <= 0) return 0;

  const peak = peakFromDose(dose);
  const timeToPeak = 3; // Assuming it takes 3 months for me to see peak levels
  const time = monthsSinceInsertion / timeToPeak;

  const bateman = peak * time * Math.exp(1 - time);
  const exhaustion = Math.exp(-DECAY_RATE * monthsSinceInsertion);

  return bateman * exhaustion;
};

const implants = [
  { inserted: localDate(2025, 7, 22), dose: 100 },
  { inserted: localDate(2025, 7, 22), dose: 100 },
  { inserted: localDate(2026, 1, 27), dose: 100 },
  { inserted: localDate(2026, 1, 27), dose: 100 },
  { inserted: localDate(2026, 7, 28), dose: 100 },
  { inserted: localDate(2026, 7, 28), dose: 100 },
];

const bloodTests = [
  { date: localDate(2025, 9, 18), level: 754 },
  { date: localDate(2026, 1, 21), level: 678 },
  { date: new Date('2026-05-19T22:58:00.000Z'), level: 1224 },
];

// Function used to calculate the DELTA between two dates
const modelLevelAt = (at: Date) =>
  implants
    .map((implant) => {
      const monthsSinceInsertion =
        (at.getTime() - implant.inserted.getTime()) / MS_PER_MONTH;
      return eFromImplant(monthsSinceInsertion, implant.dose);
    })
    .reduce((prev, cur) => prev + cur, 0);

export const dataPointsEachSide = 5;
export const getTimeSeries = () => {
  let levels: { level: number; now: Date }[] = [];

  for (let day = -dataPointsEachSide; day <= dataPointsEachSide; day++) {
    /**
     * Anchor the estimate to the most recent blood test.
     *
     * Instead of trusting the model's absolute value (which has estimation error),
     * we use the real measured level as a baseline and only ask the model:
     *   "how much has my level changed since that test?"
     *
     *   estimate = realLevel + (modelNow - modelAtTestDate)
     *
     * This way, errors in PEAK_SCALE / PEAK_EXPONENT / timeToPeak don't matter as
     * much — the model only needs to get the *shape* of the curve right.
     */
    const lastTest = bloodTests[bloodTests.length - 1];
    // Calendar-day addition, same as luxon's `plus({ day })`.
    const now = new Date();
    now.setDate(now.getDate() + day * 10);
    const delta = modelLevelAt(now) - modelLevelAt(lastTest.date);

    const level = Math.floor(lastTest.level + delta);
    levels.push({ level, now });
  }

  return levels;
};
